/**
 * Direcciones de los archivos que alojamos.
 *
 * La regla es una sola: **en la base de datos se guarda la RUTA, no la
 * dirección completa**. La dirección se compone al leerla.
 *
 * Por qué importa: la dirección lleva dentro el dominio del almacenamiento, y
 * ese dominio cambia. Hoy es el `pub-xxxx.r2.dev` de pruebas de Cloudflare;
 * mañana será uno propio. Si cada código guardara la dirección completa, ese
 * cambio obligaría a reescribir todos los códigos ya creados, uno por uno, y
 * mientras tanto los clientes verían cartas rotas. Guardando la ruta, cambiar
 * de dominio es cambiar una línea de configuración.
 *
 * Es el mismo razonamiento por el que el QR codifica un enlace corto y no el
 * destino final: se guarda la referencia estable, no la dirección del momento.
 */

/**
 * Compone la dirección pública de un archivo.
 *
 * Acepta también direcciones completas y las devuelve tal cual. No es
 * indulgencia: es lo que permite que los códigos creados antes de este cambio
 * —que sí guardaron la dirección entera— sigan funcionando sin migrar nada.
 */
export function fileUrl(
  rutaODireccion: string | null | undefined,
  base: string,
): string {
  if (!rutaODireccion) return '';

  // Dato antiguo: ya es una direccion completa.
  if (/^https?:\/\//i.test(rutaODireccion)) return rutaODireccion;

  const limpia = base.replace(/\/+$/, '');
  if (!limpia) return '';

  // Cada tramo se codifica por separado para no escapar las barras, que aqui
  // son separadores de ruta y no parte del nombre.
  const ruta = rutaODireccion.split('/').map(encodeURIComponent).join('/');
  return `${limpia}/${ruta}`;
}

/** Si un valor guardado es ya una direccion completa en vez de una ruta. */
export function esDireccionCompleta(valor: string | null | undefined): boolean {
  return Boolean(valor) && /^https?:\/\//i.test(valor as string);
}
